import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocalStorageState } from 'ahooks';
import type { ColorMode } from './theme';
export type ColorModePref = ColorMode | 'system';
const STORAGE_KEY = 'sp.color-mode';
const DARK_QUERY = '(prefers-color-scheme: dark)';
interface ColorModeContextValue {
  preference: ColorModePref;
  resolved: ColorMode;
  setPreference: (pref: ColorModePref) => void;
}
const ColorModeContext = createContext<ColorModeContextValue | null>(null);
function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_QUERY).matches;
}
export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useLocalStorageState<ColorModePref>(STORAGE_KEY, {
    defaultValue: 'system',
  });
  const preference: ColorModePref = stored ?? 'system';
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);
  useEffect(() => {
    if (preference !== 'system') return;
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent): void => setSystemDark(event.matches);
    setSystemDark(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [preference]);
  const resolved: ColorMode =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;
  useEffect(() => {
    document.documentElement.style.colorScheme = resolved;
  }, [resolved]);
  const value = useMemo<ColorModeContextValue>(
    () => ({ preference, resolved, setPreference: setStored }),
    [preference, resolved, setStored],
  );
  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}
export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  if (ctx === null) throw new Error('useColorMode 必须在 <ColorModeProvider> 内使用');
  return ctx;
}
